import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Trophy, ArrowLeft, Plus, Users, Gavel, Play, Pause, SkipForward, Shuffle, ExternalLink, Trash2, Globe, Flag, Award, Copy, Check, Download, QrCode, Upload, RotateCcw, X, FileSpreadsheet, ChevronDown, Sparkles, MessageSquare, Wand2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "../components/ui/dropdown-menu";
import CinematicPodiumReveal from "../components/CinematicPodiumReveal";
import FeedbackManager from "../components/FeedbackManager";
import { PairActionsMenu, HeatActionsMenu, AddPairDialog } from "../components/HeatPairEditor";
import ManualHeatBuilder from "../components/ManualHeatBuilder";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import axios from "axios";
import QRCode from "react-qr-code";

export default function CompetitionPage() {
  const { competitionId } = useParams();
  const { token } = useAuth();
  const [competition, setCompetition] = useState(null);
  const [dancers, setDancers] = useState([]);
  const [judges, setJudges] = useState([]);
  const [heats, setHeats] = useState([]);
  const [rankings, setRankings] = useState({ leaders: [], followers: [] });
  const [loading, setLoading] = useState(true);
  const [showAddDancerDialog, setShowAddDancerDialog] = useState(false);
  const [showAddJudgeDialog, setShowAddJudgeDialog] = useState(false);
  const [showBulkImportDialog, setShowBulkImportDialog] = useState(false);
  const [showQRDialog, setShowQRDialog] = useState(null);
  const [copiedLink, setCopiedLink] = useState(null);
  const [newDancer, setNewDancer] = useState({ name: "", role: "Leader", country: "", phone: "" });
  const [newJudge, setNewJudge] = useState({ name: "", judge_role: "Leader" });
  const [selectedRound, setSelectedRound] = useState(1);
  const [csvData, setCsvData] = useState("");
  const [advanceCount, setAdvanceCount] = useState(10);
  const [showQualifyDialog, setShowQualifyDialog] = useState(false);
  const [qualifyMode, setQualifyMode] = useState("global");
  const [qualifyLeaders, setQualifyLeaders] = useState(10);
  const [qualifyFollowers, setQualifyFollowers] = useState(10);
  const [qualifyPerHeatLeaders, setQualifyPerHeatLeaders] = useState(2);
  const [qualifyPerHeatFollowers, setQualifyPerHeatFollowers] = useState(2);
  const [rankingsView, setRankingsView] = useState("round"); // "round" | "heat" | "cumulative"
  const [byHeatData, setByHeatData] = useState(null);
  const [cumulativeData, setCumulativeData] = useState(null);
  const [showImbalanceDialog, setShowImbalanceDialog] = useState(false);
  const [secondChanceMode, setSecondChanceMode] = useState("global");
  const [fillBothRoles, setFillBothRoles] = useState(false);
  const [pendingHeatGeneration, setPendingHeatGeneration] = useState(null);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [forceShowSetup, setForceShowSetup] = useState(false);
  const [audienceQR, setAudienceQR] = useState(null);
  const [roundConfigs, setRoundConfigs] = useState({});
  const [customCriterion, setCustomCriterion] = useState("");
  const [showCinematicReveal, setShowCinematicReveal] = useState(false);
  const [addPairHeat, setAddPairHeat] = useState(null);
  const [showManualBuilder, setShowManualBuilder] = useState(false);
  const [activeHeatVoting, setActiveHeatVoting] = useState({}); // { judge_id: { voted: n, expected: m } }
  const initialSyncDone = useRef(false);

  const getAuthHeaders = () => ({
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 5000); // Poll every 5 seconds
    return () => clearInterval(interval);
  }, [competitionId, token, selectedRound]);

  const fetchAll = async () => {
    try {
      const [compRes, dancersRes, judgesRes, heatsRes, rankingsRes, configsRes] = await Promise.all([
        axios.get(`${API}/competitions/${competitionId}`, getAuthHeaders()),
        axios.get(`${API}/dancers?competition_id=${competitionId}`, getAuthHeaders()),
        axios.get(`${API}/judges?competition_id=${competitionId}`, getAuthHeaders()),
        axios.get(`${API}/heats?competition_id=${competitionId}`, getAuthHeaders()),
        axios.get(`${API}/rankings/${competitionId}?round_number=${selectedRound}`, getAuthHeaders()),
        axios.get(`${API}/competitions/${competitionId}/round-configs`, getAuthHeaders())
      ]);
      setCompetition(compRes.data);
      // Only sync selectedRound on initial load, cap at max rounds
      if (!initialSyncDone.current && compRes.data?.current_round && compRes.data.current_round > 0) {
        const maxRounds = compRes.data.rounds?.length || 3;
        setSelectedRound(Math.min(compRes.data.current_round, maxRounds));
        initialSyncDone.current = true;
      }
      setDancers(dancersRes.data);
      setJudges(judgesRes.data);
      setHeats(heatsRes.data);
      setRankings(rankingsRes.data);
      // Build round config map
      const cfgMap = {};
      (configsRes.data || []).forEach(c => { cfgMap[c.round_number] = c; });
      setRoundConfigs(cfgMap);

      // Track judge voting progress on the currently-active heat
      const liveHeat = (heatsRes.data || []).find(h => h.status === "active");
      if (liveHeat) {
        try {
          const sRes = await axios.get(`${API}/scores?heat_id=${liveHeat.heat_id}`, getAuthHeaders());
          const scored = sRes.data || [];
          const pairs = liveHeat.pairs || [];
          const leaderIds = pairs.map(p => p.leader_id).filter(Boolean);
          const followerIds = pairs.map(p => p.follower_id).filter(Boolean);
          // Per judge: which dancer_ids they've already scored in this heat
          const byJudge = {};
          scored.forEach(s => {
            byJudge[s.judge_id] = byJudge[s.judge_id] || new Set();
            byJudge[s.judge_id].add(s.dancer_id);
          });
          const out = {};
          (judgesRes.data || []).forEach(j => {
            // Judges judge dancers of their own role; "Both" judges judge everyone
            const expectedIds = j.judge_role === "Leader"
              ? leaderIds
              : j.judge_role === "Follower"
              ? followerIds
              : [...leaderIds, ...followerIds];
            const expected = expectedIds.length;
            const voted = expectedIds.filter(id => byJudge[j.judge_id]?.has(id)).length;
            out[j.judge_id] = { voted, expected, heat_id: liveHeat.heat_id };
          });
          setActiveHeatVoting(out);
        } catch (e) {
          if (process.env.NODE_ENV === 'development') console.warn("Vote-status poll failed (non-fatal):", e);
        }
      } else {
        setActiveHeatVoting({});
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddDancer = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/dancers`, { ...newDancer, competition_id: competitionId }, getAuthHeaders());
      toast.success("Dancer added!");
      setShowAddDancerDialog(false);
      setNewDancer({ name: "", role: "Leader", country: "", phone: "" });
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add dancer");
    }
  };

  const handleDeleteDancer = async (dancerId) => {
    try {
      await axios.delete(`${API}/dancers/${dancerId}`, getAuthHeaders());
      toast.success("Dancer removed");
      fetchAll();
    } catch (error) {
      toast.error("Failed to remove dancer");
    }
  };

  const handleAddJudge = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/judges`, { name: newJudge.name, judge_role: newJudge.judge_role, competition_id: competitionId }, getAuthHeaders());
      toast.success(`Judge added! Code: ${response.data.judge_code}`);
      setShowAddJudgeDialog(false);
      setNewJudge({ name: "", judge_role: "Leader" });
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add judge");
    }
  };

  const handleDeleteJudge = async (judgeId) => {
    try {
      await axios.delete(`${API}/judges/${judgeId}`, getAuthHeaders());
      toast.success("Judge removed");
      fetchAll();
    } catch (error) {
      toast.error("Failed to remove judge");
    }
  };

  const handleGenerateHeats = async () => {
    const roundNumber = selectedRound;
    try {
      await axios.post(`${API}/heats/generate?competition_id=${competitionId}&round_number=${roundNumber}`, {}, getAuthHeaders());
      toast.success(`Generated heats for Round ${roundNumber}`);
      setSelectedRound(roundNumber);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate heats");
    }
  };

  const handleStartRound = async () => {
    const roundNumber = selectedRound;
    try {
      await axios.post(`${API}/heats/start-round?competition_id=${competitionId}&round_number=${roundNumber}`, {}, getAuthHeaders());
      toast.success("Round started!");
      fetchAll();
    } catch (error) {
      toast.error("Failed to start round");
    }
  };

  const handleNextHeat = async () => {
    try {
      const response = await axios.post(`${API}/heats/next?competition_id=${competitionId}`, {}, getAuthHeaders());
      if (response.data.round_complete) {
        toast.success("Round complete!");
      } else {
        toast.success(`Moving to Heat ${response.data.heat_number}`);
      }
      fetchAll();
    } catch (error) {
      toast.error("Failed to advance heat");
    }
  };

  const handleUpdateStatus = async (status) => {
    try {
      await axios.put(`${API}/competitions/${competitionId}/status?status=${status}`, {}, getAuthHeaders());
      toast.success(`Competition ${status}`);
      fetchAll();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const copyJudgeLink = (judge) => {
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/judge-login?event=${judge.event_code || ''}&judge=${judge.judge_code || ''}`;
    navigator.clipboard.writeText(link);
    setCopiedLink(judge.judge_id);
    setTimeout(() => setCopiedLink(null), 2000);
    toast.success("Judge login link copied!");
  };

  const downloadPDF = () => {
    window.open(`${API}/export/results/${competitionId}`, '_blank');
  };

  const downloadCompetitorsPDF = () => {
    window.open(`${API}/export/competitors/${competitionId}`, '_blank');
  };

  const downloadResultsRoundPDF = () => {
    window.open(`${API}/export/results-filtered/${competitionId}?round_number=${selectedRound}`, '_blank');
  };

  const downloadResultsXLSX = (roundOnly = false) => {
    const qs = roundOnly ? `?round_number=${selectedRound}` : "";
    window.open(`${API}/export/results-xlsx/${competitionId}${qs}`, '_blank');
  };

  const downloadCompetitorsXLSX = () => {
    window.open(`${API}/export/competitors-xlsx/${competitionId}`, '_blank');
  };

  const downloadHeatPDF = (heatId) => {
    window.open(`${API}/export/results-filtered/${competitionId}?heat_id=${heatId}`, '_blank');
  };

  const downloadHeatXLSX = (heatId) => {
    window.open(`${API}/export/results-xlsx/${competitionId}?heat_id=${heatId}`, '_blank');
  };

  const handleToggleJudgeDancing = async (enabled) => {
    try {
      await axios.put(
        `${API}/competitions/${competitionId}/judge-dancing?enabled=${enabled}`,
        {},
        getAuthHeaders()
      );
      toast.success(`Judge Dancing ${enabled ? "enabled" : "disabled"}`);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update");
    }
  };

  const handleGenerateJudgeDancingHeats = async () => {
    if (!window.confirm(
      "This will REPLACE existing heats in the Final round with 2 special judge-dancing heats. Continue?"
    )) return;
    try {
      const r = await axios.post(
        `${API}/competitions/${competitionId}/heats/generate-judge-dancing`,
        {},
        getAuthHeaders()
      );
      toast.success(r.data.message);
      setSelectedRound(r.data.round_number);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate");
    }
  };

  const handleBulkImport = async () => {
    if (!csvData.trim()) {
      toast.error("Please enter CSV data");
      return;
    }
    try {
      const response = await axios.post(`${API}/dancers/bulk-import`, {
        competition_id: competitionId,
        csv_data: csvData
      }, getAuthHeaders());
      toast.success(response.data.message);
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} rows had errors`);
      }
      setShowBulkImportDialog(false);
      setCsvData("");
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    }
  };

  const handleCreateHeats = async (numHeats, couplesPerHeat) => {
    const roundNum = selectedRound || competition?.current_round || 1;
    // Check imbalance BEFORE generating
    const advancingLeaders = dancers.filter(d => d.role === "Leader" && d.is_advancing !== false).length;
    const advancingFollowers = dancers.filter(d => d.role === "Follower" && d.is_advancing !== false).length;
    if (Math.abs(advancingLeaders - advancingFollowers) > 0) {
      // Show modal to choose strategy (works for round 1 too: volunteer fillers or single role).
      setPendingHeatGeneration({ numHeats, couplesPerHeat, roundNum, advancingLeaders, advancingFollowers });
      setShowImbalanceDialog(true);
      return;
    }
    await doGenerateHeats(numHeats, couplesPerHeat, roundNum, "volunteers", null);
  };

  const doGenerateHeats = async (numHeats, couplesPerHeat, roundNum, strategy, singleRole, opts = {}) => {
    try {
      const response = await axios.post(
        `${API}/competitions/${competitionId}/heats/generate-structured`,
        {
          num_heats: numHeats,
          couples_per_heat: couplesPerHeat,
          round_number: roundNum,
          imbalance_strategy: strategy,
          single_role: singleRole,
          second_chance_mode: opts.second_chance_mode || "global",
          fill_both_roles: !!opts.fill_both_roles,
        },
        getAuthHeaders()
      );
      toast.success(response.data.message);
      setSelectedRound(response.data.round_number || roundNum);
      setForceShowSetup(false);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create heats");
    }
  };

  const handleShowQR = (judge) => {
    // Generate QR URL using current browser origin - works after deploy!
    const baseUrl = window.location.origin;
    const loginUrl = `${baseUrl}/judge-login?event=${judge.event_code || ''}&judge=${judge.judge_code || ''}`;
    setShowQRDialog({ judgeId: judge.judge_id, judgeName: judge.name, loginUrl });
  };

  const fetchByHeatRankings = async () => {
    setRankingsView("heat");
    try {
      const rnd = selectedRound || competition?.current_round || 1;
      const r = await axios.get(`${API}/rankings/${competitionId}/by-heat?round_number=${rnd}`, getAuthHeaders());
      setByHeatData(r.data);
    } catch (error) {
      toast.error("Failed to load per-heat rankings");
    }
  };

  const fetchCumulativeRankings = async () => {
    setRankingsView("cumulative");
    try {
      const r = await axios.get(`${API}/rankings/${competitionId}/cumulative`, getAuthHeaders());
      setCumulativeData(r.data);
    } catch (error) {
      toast.error("Failed to load cumulative rankings");
    }
  };

  const handleUpdateBib = async (dancer, newBib) => {
    const n = parseInt(newBib, 10);
    if (!n || n < 1) {
      toast.error("Bib number must be a positive integer");
      return;
    }
    try {
      await axios.put(
        `${API}/dancers/${dancer.dancer_id}`,
        {
          competition_id: dancer.competition_id,
          name: dancer.name,
          role: dancer.role,
          country: dancer.country || "",
          phone: dancer.phone || "",
          email: dancer.email || "",
          bib_number: n,
        },
        getAuthHeaders()
      );
      toast.success(`Bib updated to #${n}`);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update bib");
    }
  };

  const promptEditBib = (dancer) => {
    if (heats.length > 0) {
      toast.error("Bib numbers can only be edited before heats are generated.");
      return;
    }
    const next = window.prompt(`Edit bib number for ${dancer.name}:`, dancer.bib_number || "");
    if (next == null) return; // cancelled
    handleUpdateBib(dancer, next);
  };

  const handleAdvanceDancers = async (params = {}) => {
    try {
      const sp = new URLSearchParams();
      const mode = params.mode || "global";
      sp.set("mode", mode);
      if (mode === "global") {
        if (params.count_leaders != null) sp.set("count_leaders", String(params.count_leaders));
        if (params.count_followers != null) sp.set("count_followers", String(params.count_followers));
        if (params.count != null && params.count_leaders == null) sp.set("count", String(params.count));
      } else if (mode === "per_heat") {
        sp.set("per_heat_leaders", String(params.per_heat_leaders || 0));
        sp.set("per_heat_followers", String(params.per_heat_followers || 0));
      }
      const response = await axios.post(
        `${API}/competitions/${competitionId}/advance-dancers?${sp.toString()}`,
        {},
        getAuthHeaders()
      );
      toast.success(response.data.message);
      if (response.data.new_round) {
        const maxRounds = competition?.rounds?.length || 3;
        setSelectedRound(Math.min(response.data.new_round, maxRounds));
      }
      setShowQualifyDialog(false);
      fetchAll();
    } catch (error) {
      const detail = error.response?.data?.detail || "Failed to advance dancers";
      toast.error(detail);
    }
  };

  const handleResetCompetition = async () => {
    try {
      await axios.post(`${API}/competitions/${competitionId}/reset`, {}, getAuthHeaders());
      toast.success("Competition has been reset!");
      setShowResetDialog(false);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset competition");
    }
  };

  const handleShowAudienceQR = () => {
    // Generate QR URL using current browser origin - works after deploy!
    const baseUrl = window.location.origin;
    const votingUrl = `${baseUrl}/vote/${competitionId}`;
    setAudienceQR({ voting_url: votingUrl });
  };

  const handleSetRoundConfig = async (roundNum, mode, criteria = null, judgingMode = null) => {
    try {
      const currentConfig = roundConfigs[roundNum] || {};
      const payload = {
        round_number: roundNum,
        scoring_mode: mode,
        judging_mode: judgingMode || currentConfig.judging_mode || "full",
        criteria: mode === "single" ? null : (criteria || currentConfig.criteria || null)
      };
      const res = await axios.post(`${API}/competitions/${competitionId}/round-config`, payload, getAuthHeaders());
      setRoundConfigs(prev => ({ ...prev, [roundNum]: res.data }));
      toast.success(`Round config updated`);
    } catch (error) {
      toast.error("Failed to update scoring config");
    }
  };

  const handleAddCustomCriterion = () => {
    const name = customCriterion.trim().toLowerCase();
    if (!name) return;
    const currentConfig = roundConfigs[selectedRound] || {};
    const currentCriteria = currentConfig.criteria || ["connection", "technique", "musicality", "presence"];
    if (currentCriteria.includes(name)) {
      toast.error("Criterion already exists");
      return;
    }
    handleSetRoundConfig(selectedRound, "multiple", [...currentCriteria, name]);
    setCustomCriterion("");
  };

  const handleRemoveCriterion = (criterion) => {
    const currentConfig = roundConfigs[selectedRound] || {};
    const currentCriteria = (currentConfig.criteria || []).filter(c => c !== criterion);
    if (currentCriteria.length === 0) {
      toast.error("Must have at least one criterion");
      return;
    }
    handleSetRoundConfig(selectedRound, "multiple", currentCriteria);
  };

  const handleToggleJudgingMode = (mode) => {
    const currentConfig = roundConfigs[selectedRound] || {};
    handleSetRoundConfig(selectedRound, currentConfig.scoring_mode || "single", currentConfig.criteria, mode);
  };


  const handleCompleteRound = async () => {
    try {
      // Mark all pending/active heats in current round as scored
      const roundHeats = heats.filter(h => h.round_number === selectedRound);
      const pendingOrActive = roundHeats.filter(h => h.status === 'pending' || h.status === 'active');
      
      for (const heat of pendingOrActive) {
        await axios.put(`${API}/heats/${heat.heat_id}/status?status=scored`, {}, getAuthHeaders());
      }
      
      toast.success("Round completed! All heats marked as scored.");
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to complete round");
    }
  };

  const handleAddNewHeat = async () => {
    try {
      const round = selectedRound;
      const response = await axios.post(`${API}/heats/add-single?competition_id=${competitionId}&round_number=${round}`, {}, getAuthHeaders());
      toast.success(response.data.message);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add heat");
    }
  };

  const allLeaders = dancers.filter(d => d.role === "Leader");
  const allFollowers = dancers.filter(d => d.role === "Follower");
  const advancingLeaders = dancers.filter(d => d.role === "Leader" && d.is_advancing);
  const advancingFollowers = dancers.filter(d => d.role === "Follower" && d.is_advancing);
  // For Setup Heats: show advancing dancers only if round > 1
  const readyLeaders = selectedRound > 1 ? advancingLeaders : allLeaders;
  const readyFollowers = selectedRound > 1 ? advancingFollowers : allFollowers;
  const currentRoundHeats = heats.filter(h => h.round_number === selectedRound);
  const activeHeat = heats.find(h => h.status === "active");
  const isFinalRound = competition?.rounds && selectedRound >= competition.rounds.length;
  const currentRoundName = competition?.rounds?.[selectedRound - 1] || `Round ${selectedRound}`;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#FFD700] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b]">
      {/* Header */}
      <header className="glass sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to={`/festival/${competition?.festival_id}`} className="text-[#A1A1AA] hover:text-white" data-testid="back-btn">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <div>
              <h1 className="font-['Playfair_Display'] text-xl font-bold text-white">{competition?.category}</h1>
              <span className={`text-xs font-medium uppercase ${
                competition?.status === "active" ? "text-[#FF007F]" : "text-[#A1A1AA]"
              }`}>
                {competition?.status === "active" && "● "}{competition?.status}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to={`/now-dancing/${competitionId}`} target="_blank" data-testid="live-display-now-dancing-link">
              <Button variant="outline" className="btn-outline" size="sm">
                <ExternalLink className="w-4 h-4 mr-2" />
                Live: Now Dancing
              </Button>
            </Link>
            <Link to={`/results/${competitionId}`} target="_blank" data-testid="public-results-link">
              <Button variant="outline" className="btn-outline" size="sm">
                <Globe className="w-4 h-4 mr-2" />
                Public Results
              </Button>
            </Link>
            <Button onClick={() => setShowCinematicReveal(true)} className="btn-gold" size="sm" data-testid="cinematic-reveal-btn">
              <Sparkles className="w-4 h-4 mr-2" />
              Podium Reveal
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="btn-outline" size="sm" data-testid="export-menu-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Export
                  <ChevronDown className="w-3 h-3 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#0f0f12] border-white/10 text-white w-64">
                <DropdownMenuLabel className="text-[#A1A1AA]">Results — PDF</DropdownMenuLabel>
                <DropdownMenuItem onClick={downloadPDF} data-testid="export-results-pdf-all">
                  <Download className="w-4 h-4 mr-2 text-[#FFD700]" />
                  All rounds (overall)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={downloadResultsRoundPDF} data-testid="export-results-pdf-round">
                  <Download className="w-4 h-4 mr-2 text-[#FFD700]" />
                  Selected round only
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuLabel className="text-[#A1A1AA]">Results — Excel</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => downloadResultsXLSX(false)} data-testid="export-results-xlsx-all">
                  <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-400" />
                  All rounds (overall)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => downloadResultsXLSX(true)} data-testid="export-results-xlsx-round">
                  <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-400" />
                  Selected round only
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuLabel className="text-[#A1A1AA]">Competitors</DropdownMenuLabel>
                <DropdownMenuItem onClick={downloadCompetitorsPDF} data-testid="export-competitors-pdf">
                  <Users className="w-4 h-4 mr-2 text-[#FFD700]" />
                  Competitors PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={downloadCompetitorsXLSX} data-testid="export-competitors-xlsx">
                  <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-400" />
                  Competitors Excel
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuLabel className="text-[#A1A1AA]">Heat exports</DropdownMenuLabel>
                {heats.filter(h => h.round_number === selectedRound).length === 0 ? (
                  <DropdownMenuItem disabled className="text-[#52525B]">
                    No heats in selected round
                  </DropdownMenuItem>
                ) : (
                  heats.filter(h => h.round_number === selectedRound).map((h) => (
                    <DropdownMenuItem
                      key={h.heat_id}
                      onClick={() => downloadHeatPDF(h.heat_id)}
                      data-testid={`export-heat-pdf-${h.heat_number}`}
                    >
                      <Download className="w-4 h-4 mr-2 text-[#FFD700]" />
                      Heat #{h.heat_number} (PDF)
                      <button
                        onClick={(e) => { e.stopPropagation(); downloadHeatXLSX(h.heat_id); }}
                        className="ml-auto text-emerald-400 hover:text-emerald-300"
                        title="Excel"
                        data-testid={`export-heat-xlsx-${h.heat_number}`}
                      >
                        <FileSpreadsheet className="w-4 h-4" />
                      </button>
                    </DropdownMenuItem>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            {competition?.audience_voting_enabled && (
              <Button onClick={handleShowAudienceQR} variant="outline" className="btn-outline" size="sm" data-testid="audience-qr-btn">
                <QrCode className="w-4 h-4 mr-2" />
                Audience QR
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Control Panel */}
        <div className="glass p-6 rounded-xl mb-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div>
                <p className="text-[#A1A1AA] text-sm">Round</p>
                <Select value={String(selectedRound)} onValueChange={(v) => setSelectedRound(parseInt(v))}>
                  <SelectTrigger className="w-32 bg-black/20 border-white/10 text-white" data-testid="round-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-white/10">
                    {competition?.rounds.map((round, i) => (
                      <SelectItem key={i} value={String(i + 1)}>{round}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {activeHeat && (
                <div className="bg-[#FF007F]/20 px-4 py-2 rounded-lg" data-testid="active-heat-panel">
                  <p className="text-[#FF007F] text-sm font-medium">Active Heat {activeHeat.heat_number}</p>
                  <p className="text-white text-sm">{activeHeat.leader_name} & {activeHeat.follower_name}</p>
                  {judges.length > 0 && (
                    <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="active-heat-judge-status">
                      {judges.map(j => {
                        const v = activeHeatVoting[j.judge_id];
                        const done = v && v.expected > 0 && v.voted >= v.expected;
                        const inProgress = v && v.voted > 0 && v.voted < v.expected;
                        const initials = (j.name || "?").split(" ").map(s => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
                        return (
                          <div
                            key={j.judge_id}
                            title={`${j.name} (${j.judge_role}) — ${v ? `${v.voted}/${v.expected} scored` : "no scores yet"}`}
                            className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium border ${
                              done
                                ? "bg-green-500/20 text-green-400 border-green-500/40"
                                : inProgress
                                ? "bg-[#FFD700]/20 text-[#FFD700] border-[#FFD700]/40"
                                : "bg-white/5 text-[#A1A1AA] border-white/10"
                            }`}
                            data-testid={`judge-vote-status-${j.judge_id}`}
                          >
                            <span className="font-mono">{initials}</span>
                            {done ? (
                              <Check className="w-3 h-3" />
                            ) : (
                              <span className="font-mono text-[10px] opacity-80">{v ? `${v.voted}/${v.expected}` : "0/?"}</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-3">
              <Button onClick={handleStartRound} variant="outline" className="btn-outline" data-testid="start-round-btn">
                <Play className="w-4 h-4 mr-2" />
                Start Round
              </Button>
              <Button onClick={handleNextHeat} className="btn-gold" data-testid="next-heat-btn">
                <SkipForward className="w-4 h-4 mr-2" />
                Next Heat
              </Button>
              <Button onClick={handleCompleteRound} variant="outline" className="btn-outline" data-testid="complete-round-btn">
                <Check className="w-4 h-4 mr-2" />
                Complete Round
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t border-white/10">
            <Button onClick={() => handleUpdateStatus("active")} size="sm" className="bg-[#FF007F]/20 text-[#FF007F] hover:bg-[#FF007F]/30">
              Set Active
            </Button>
            <Button onClick={() => handleUpdateStatus("paused")} size="sm" className="bg-[#FFD700]/20 text-[#FFD700] hover:bg-[#FFD700]/30">
              Pause
            </Button>
            <Button onClick={() => handleUpdateStatus("completed")} size="sm" className="bg-green-500/20 text-green-500 hover:bg-green-500/30">
              Complete
            </Button>
            <div className="ml-auto">
              <Button onClick={() => setShowResetDialog(true)} size="sm" variant="outline" className="border-red-500/30 text-red-500 hover:bg-red-500/20" data-testid="reset-competition-btn">
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset Competition
              </Button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="dancers" className="space-y-6">
          <TabsList className="bg-[#18181b] border border-white/10">
            <TabsTrigger value="dancers" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black">
              <Users className="w-4 h-4 mr-2" />
              Dancers ({readyLeaders.length + readyFollowers.length})
            </TabsTrigger>
            <TabsTrigger value="judges" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black">
              <Gavel className="w-4 h-4 mr-2" />
              Judges ({judges.length})
            </TabsTrigger>
            <TabsTrigger value="heats" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black">
              <Play className="w-4 h-4 mr-2" />
              Heats ({currentRoundHeats.length})
            </TabsTrigger>
            <TabsTrigger value="rankings" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black">
              <Award className="w-4 h-4 mr-2" />
              Rankings
            </TabsTrigger>
            <TabsTrigger value="feedback" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black" data-testid="tab-feedback">
              <MessageSquare className="w-4 h-4 mr-2" />
              Feedback
            </TabsTrigger>
          </TabsList>

          {/* Dancers Tab */}
          <TabsContent value="dancers">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
              <div className="flex gap-4">
                <span className="text-[#A1A1AA]">Leaders: <strong className="text-white">{readyLeaders.length}</strong></span>
                <span className="text-[#A1A1AA]">Followers: <strong className="text-white">{readyFollowers.length}</strong></span>
              </div>
              <div className="flex gap-2">
                <Dialog open={showBulkImportDialog} onOpenChange={setShowBulkImportDialog}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="btn-outline" data-testid="bulk-import-btn">
                      <Upload className="w-4 h-4 mr-2" />
                      Import CSV
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#18181b] border-white/10 text-white max-w-lg">
                    <DialogHeader>
                      <DialogTitle className="font-['Playfair_Display'] text-2xl text-white">Bulk Import Dancers</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label className="text-[#A1A1AA]">CSV Format</Label>
                        <p className="text-[#52525B] text-sm mb-2">
                          Headers: name, role, country
                        </p>
                        <code className="block bg-black/40 p-3 rounded text-xs text-[#A1A1AA]">
                          name,role,country<br/>
                          Carlos Martinez,Leader,Spain<br/>
                          Maria Garcia,Follower,Spain
                        </code>
                      </div>
                      <div>
                        <Label className="text-[#A1A1AA]">Paste CSV Data</Label>
                        <Textarea
                          value={csvData}
                          onChange={(e) => setCsvData(e.target.value)}
                          placeholder="name,role,country&#10;John Doe,Leader,USA"
                          className="mt-1 bg-black/20 border-white/10 text-white h-40 font-mono text-sm"
                          data-testid="csv-input"
                        />
                      </div>
                      <Button onClick={handleBulkImport} className="w-full btn-gold" data-testid="confirm-import-btn">
                        <Upload className="w-4 h-4 mr-2" />
                        Import Dancers
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
                <Dialog open={showAddDancerDialog} onOpenChange={setShowAddDancerDialog}>
                  <DialogTrigger asChild>
                    <Button className="btn-gold" data-testid="add-dancer-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Dancer
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#18181b] border-white/10 text-white">
                    <DialogHeader>
                      <DialogTitle className="font-['Playfair_Display'] text-2xl text-white">Add Dancer</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddDancer} className="space-y-4 mt-4">
                      <div>
                        <Label className="text-[#A1A1AA]">Name</Label>
                        <Input
                          value={newDancer.name}
                          onChange={(e) => setNewDancer({ ...newDancer, name: e.target.value })}
                          placeholder="Carlos Martinez"
                          className="mt-1 bg-black/20 border-white/10 text-white"
                          required
                          data-testid="dancer-name-input"
                        />
                      </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-[#A1A1AA]">Role</Label>
                        <Select value={newDancer.role} onValueChange={(v) => setNewDancer({ ...newDancer, role: v })}>
                          <SelectTrigger className="mt-1 bg-black/20 border-white/10 text-white" data-testid="dancer-role-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#18181b] border-white/10">
                            <SelectItem value="Leader">Leader</SelectItem>
                            <SelectItem value="Follower">Follower</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-[#A1A1AA]">Country</Label>
                        <Input
                          value={newDancer.country}
                          onChange={(e) => setNewDancer({ ...newDancer, country: e.target.value })}
                          placeholder="Spain"
                          className="mt-1 bg-black/20 border-white/10 text-white"
                          data-testid="dancer-country-input"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-[#A1A1AA]">Phone</Label>
                      <Input
                        value={newDancer.phone}
                        onChange={(e) => setNewDancer({ ...newDancer, phone: e.target.value })}
                        placeholder="+30 6912345678"
                        type="tel"
                        className="mt-1 bg-black/20 border-white/10 text-white"
                        data-testid="dancer-phone-input"
                      />
                    </div>
                    <Button type="submit" className="w-full btn-gold py-3" data-testid="submit-dancer-btn">
                      Add Dancer
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Leaders */}
              <div className="glass p-6 rounded-xl">
                <h3 className="font-['Playfair_Display'] text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#FFD700]"></div>
                  Leaders ({readyLeaders.length})
                </h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {readyLeaders.map((dancer) => (
                    <div key={dancer.dancer_id} className="flex items-center justify-between p-3 bg-black/20 rounded-lg" data-testid={`dancer-${dancer.dancer_id}`}>
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => promptEditBib(dancer)}
                          className="w-10 h-10 rounded-lg bg-[#FFD700]/20 flex items-center justify-center hover:ring-2 hover:ring-[#FFD700] transition"
                          title={heats.length > 0 ? "Bibs locked once heats are created" : "Click to edit bib"}
                          data-testid={`edit-bib-${dancer.dancer_id}`}
                        >
                          <span className="text-[#FFD700] font-bold text-lg">#{dancer.bib_number || '?'}</span>
                        </button>
                        <div>
                          <p className="text-white font-medium">{dancer.name}</p>
                          <p className="text-[#A1A1AA] text-sm flex items-center gap-2">
                            <Flag className="w-3 h-3" /> {dancer.country || 'Unknown'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-1 rounded text-xs ${dancer.is_advancing ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}`}>
                          {dancer.is_advancing ? 'In' : 'Out'}
                        </span>
                        <button onClick={() => handleDeleteDancer(dancer.dancer_id)} className="text-[#52525B] hover:text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                  {readyLeaders.length === 0 && <p className="text-[#52525B] text-center py-4">No leaders in this round</p>}
                </div>
              </div>
              {/* Followers */}
              <div className="glass p-6 rounded-xl">
                <h3 className="font-['Playfair_Display'] text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#FF007F]"></div>
                  Followers ({readyFollowers.length})
                </h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {readyFollowers.map((dancer) => (
                    <div key={dancer.dancer_id} className="flex items-center justify-between p-3 bg-black/20 rounded-lg" data-testid={`dancer-${dancer.dancer_id}`}>
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => promptEditBib(dancer)}
                          className="w-10 h-10 rounded-lg bg-[#FF007F]/20 flex items-center justify-center hover:ring-2 hover:ring-[#FF007F] transition"
                          title={heats.length > 0 ? "Bibs locked once heats are created" : "Click to edit bib"}
                          data-testid={`edit-bib-${dancer.dancer_id}`}
                        >
                          <span className="text-[#FF007F] font-bold text-lg">#{dancer.bib_number || '?'}</span>
                        </button>
                        <div>
                          <p className="text-white font-medium">{dancer.name}</p>
                          <p className="text-[#A1A1AA] text-sm flex items-center gap-2">
                            <Flag className="w-3 h-3" /> {dancer.country || 'Unknown'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`px-2 py-1 rounded text-xs ${dancer.is_advancing ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}`}>
                          {dancer.is_advancing ? 'In' : 'Out'}
                        </span>
                        <button onClick={() => handleDeleteDancer(dancer.dancer_id)} className="text-[#52525B] hover:text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                  {readyFollowers.length === 0 && <p className="text-[#52525B] text-center py-4">No followers in this round</p>}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Judges Tab */}
          <TabsContent value="judges">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
              <div>
                <p className="text-[#A1A1AA]">Total Judges: <strong className="text-white">{judges.length}</strong></p>
              </div>
              <div className="flex gap-2">
                <Dialog open={showAddJudgeDialog} onOpenChange={setShowAddJudgeDialog}>
                  <DialogTrigger asChild>
                    <Button className="btn-gold" data-testid="add-judge-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Judge
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#18181b] border-white/10 text-white">
                    <DialogHeader>
                      <DialogTitle className="font-['Playfair_Display'] text-2xl text-white">Add Judge</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddJudge} className="space-y-4 mt-4">
                      <div>
                        <Label className="text-[#A1A1AA]">Name</Label>
                        <Input
                          value={newJudge.name}
                          onChange={(e) => setNewJudge({ ...newJudge, name: e.target.value })}
                          placeholder="Judge name"
                          className="mt-1 bg-black/20 border-white/10 text-white"
                          required
                          data-testid="judge-name-input"
                        />
                      </div>
                      <div>
                        <Label className="text-[#A1A1AA]">Judge Role</Label>
                        <Select value={newJudge.judge_role} onValueChange={(v) => setNewJudge({ ...newJudge, judge_role: v })}>
                          <SelectTrigger className="mt-1 bg-black/20 border-white/10 text-white" data-testid="judge-role-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#18181b] border-white/10">
                            <SelectItem value="Leader">Leader Judge</SelectItem>
                            <SelectItem value="Follower">Follower Judge</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-[#52525B] text-xs mt-1">Used for role-based judging assignment</p>
                      </div>
                      <div className="p-4 bg-black/30 rounded-lg">
                        <p className="text-[#A1A1AA] text-sm">
                          A unique <strong className="text-[#FFD700]">Judge Code</strong> will be generated automatically.
                        </p>
                      </div>
                      <Button type="submit" className="w-full btn-gold py-3" data-testid="submit-judge-btn">
                        Create Judge
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {judges.map((judge) => (
                <motion.div
                  key={judge.judge_id}
                  className="glass p-6 rounded-xl"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-testid={`judge-card-${judge.judge_id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-white font-medium text-lg">{judge.name}</h3>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${judge.judge_role === 'Follower' ? 'bg-[#FF007F]/20 text-[#FF007F]' : 'bg-[#FFD700]/20 text-[#FFD700]'}`}>
                          {judge.judge_role || 'Leader'} Judge
                        </span>
                        {activeHeat && activeHeatVoting[judge.judge_id] && (() => {
                          const v = activeHeatVoting[judge.judge_id];
                          const done = v.expected > 0 && v.voted >= v.expected;
                          const inProgress = v.voted > 0 && v.voted < v.expected;
                          return (
                            <span
                              className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${
                                done
                                  ? "bg-green-500/15 text-green-400 border-green-500/40"
                                  : inProgress
                                  ? "bg-[#FFD700]/15 text-[#FFD700] border-[#FFD700]/40"
                                  : "bg-white/5 text-[#71717A] border-white/10"
                              }`}
                              title={`Voting status for Heat ${activeHeat.heat_number}`}
                              data-testid={`judge-card-vote-${judge.judge_id}`}
                            >
                              {done ? (
                                <>
                                  <Check className="w-3 h-3" />
                                  Voted
                                </>
                              ) : (
                                <>
                                  <span className="w-2 h-2 rounded-full bg-current" />
                                  Heat {activeHeat.heat_number}: {v.voted}/{v.expected}
                                </>
                              )}
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                    <button onClick={() => handleDeleteJudge(judge.judge_id)} className="text-[#52525B] hover:text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  
                  {/* Judge Code - prominently displayed */}
                  <div className="bg-black/30 rounded-lg p-4 mb-4">
                    <p className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-1">Judge Code</p>
                    <p className="font-mono text-2xl font-bold text-[#FFD700] tracking-widest">{judge.judge_code}</p>
                  </div>
                  
                  <div className="text-sm text-[#52525B]">
                    <p>Give this code to the judge.</p>
                    <p>They will login at <span className="text-[#00E5FF]">/judge-login</span></p>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <Link to={`/judge/${judge.judge_id}`} target="_blank">
                      <Button variant="outline" className="w-full btn-outline" size="sm">
                        <ExternalLink className="w-4 h-4 mr-1" />
                        Open
                      </Button>
                    </Link>
                    <Button 
                      variant="outline" 
                      className="btn-outline" 
                      size="sm"
                      onClick={() => handleShowQR(judge)}
                      data-testid={`qr-${judge.judge_id}`}
                    >
                      <QrCode className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      className="btn-outline" 
                      size="sm"
                      onClick={() => copyJudgeLink(judge)}
                      data-testid={`copy-link-${judge.judge_id}`}
                    >
                      {copiedLink === judge.judge_id ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </motion.div>
              ))}
              {judges.length === 0 && (
                <div className="col-span-full text-center py-12 glass rounded-xl">
                  <Gavel className="w-12 h-12 text-[#27272a] mx-auto mb-4" />
                  <p className="text-[#A1A1AA]">No judges added yet</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Heats Tab */}
          <TabsContent value="heats">
            {/* Judge Dancing Final — Setup */}
            {competition?.rounds && selectedRound === competition.rounds.length && (
              <div className="glass rounded-xl p-4 mb-4 border border-[#FFD700]/20" data-testid="judge-dancing-card">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#FFD700]/15 flex items-center justify-center">
                      <Wand2 className="w-5 h-5 text-[#FFD700]" />
                    </div>
                    <div>
                      <p className="text-white font-bold">Judge Dancing — Final Round</p>
                      <p className="text-[#A1A1AA] text-xs max-w-md">
                        Special 2-heat format where judges dance with finalists. One heat showcases Followers (judge-Leaders dance), the next showcases Leaders. Free voting — any judge can score.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        className="accent-[#FFD700] w-4 h-4"
                        checked={!!competition?.judge_dancing_enabled}
                        onChange={(e) => handleToggleJudgeDancing(e.target.checked)}
                        data-testid="judge-dancing-toggle"
                      />
                      <span className="text-white text-sm">Enable</span>
                    </label>
                    <Button
                      size="sm"
                      className="btn-gold"
                      disabled={!competition?.judge_dancing_enabled}
                      onClick={handleGenerateJudgeDancingHeats}
                      data-testid="generate-judge-dancing-btn"
                    >
                      <Wand2 className="w-4 h-4 mr-1" />
                      Generate Judge-Dancing Heats
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Round Scoring Configuration */}
            <div className="glass p-5 rounded-xl mb-6">
              <h3 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
                <Award className="w-5 h-5 text-[#FFD700]" />
                Scoring Configuration - {currentRoundName}
              </h3>

              {/* Judging Mode */}
              <div className="mb-4">
                <Label className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-2 block">Judging Assignment</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => handleToggleJudgingMode("full")}
                    className={`p-3 rounded-lg border text-sm transition-all ${
                      (roundConfigs[selectedRound]?.judging_mode || "full") === "full"
                        ? "border-[#00E5FF] bg-[#00E5FF]/10 text-[#00E5FF]" : "border-white/10 text-[#A1A1AA] hover:border-white/20"
                    }`}
                    data-testid="judging-mode-full"
                  >
                    <p className="font-medium">Full Judging</p>
                    <p className="text-xs opacity-70 mt-0.5">All judges evaluate all dancers</p>
                  </button>
                  <button
                    onClick={() => handleToggleJudgingMode("role_based")}
                    className={`p-3 rounded-lg border text-sm transition-all ${
                      roundConfigs[selectedRound]?.judging_mode === "role_based"
                        ? "border-[#A855F7] bg-[#A855F7]/10 text-[#A855F7]" : "border-white/10 text-[#A1A1AA] hover:border-white/20"
                    }`}
                    data-testid="judging-mode-role-based"
                  >
                    <p className="font-medium">Role-Based</p>
                    <p className="text-xs opacity-70 mt-0.5">Leader judges → Leaders, Follower judges → Followers</p>
                  </button>
                </div>
              </div>

              {/* Scoring Mode */}
              <Label className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-2 block">Scoring Mode</Label>
              <div className="grid grid-cols-2 gap-2 mb-4">
                <button
                  onClick={() => handleSetRoundConfig(selectedRound, "single")}
                  className={`p-3 rounded-lg border text-sm text-left transition-all ${
                    (roundConfigs[selectedRound]?.scoring_mode || "single") === "single"
                      ? "border-[#FFD700] bg-[#FFD700]/10" : "border-white/10 hover:border-white/20"
                  }`}
                  data-testid="scoring-mode-single"
                >
                  <p className="text-white font-medium">Single Criterion</p>
                  <p className="text-[#A1A1AA] text-xs">Overall score only (5-10)</p>
                </button>
                <button
                  onClick={() => handleSetRoundConfig(selectedRound, "multiple")}
                  className={`p-3 rounded-lg border text-sm text-left transition-all ${
                    roundConfigs[selectedRound]?.scoring_mode === "multiple"
                      ? "border-[#FF007F] bg-[#FF007F]/10" : "border-white/10 hover:border-white/20"
                  }`}
                  data-testid="scoring-mode-multiple"
                >
                  <p className="text-white font-medium">Multiple Criteria</p>
                  <p className="text-[#A1A1AA] text-xs">Score each criterion (5-10)</p>
                </button>
              </div>

              {/* Active Criteria (when multiple) */}
              {roundConfigs[selectedRound]?.scoring_mode === "multiple" && (
                <div className="p-3 bg-black/20 rounded-lg border border-white/5">
                  <Label className="text-[#A1A1AA] text-xs uppercase tracking-wider mb-2 block">Active Criteria</Label>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {(roundConfigs[selectedRound]?.criteria || ["connection", "technique", "musicality", "presence"]).map(c => (
                      <span key={c} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#FF007F]/10 border border-[#FF007F]/30 rounded-full text-sm text-white capitalize">
                        {c}
                        <button onClick={() => handleRemoveCriterion(c)} className="hover:text-red-400 ml-1">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={customCriterion}
                      onChange={(e) => setCustomCriterion(e.target.value)}
                      placeholder="Add custom criterion..."
                      className="bg-black/30 border-white/10 text-white text-sm h-8"
                      onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddCustomCriterion())}
                      data-testid="custom-criterion-input"
                    />
                    <Button size="sm" variant="outline" className="btn-outline h-8 text-xs" onClick={handleAddCustomCriterion} data-testid="add-criterion-btn">
                      <Plus className="w-3 h-3 mr-1" /> Add
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Heat Setup Section */}
            {(currentRoundHeats.length === 0 || forceShowSetup) ? (
              <div className="glass p-8 rounded-xl text-center">
                {forceShowSetup && currentRoundHeats.length > 0 && (
                  <div className="mb-4 p-3 bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-lg text-left">
                    <p className="text-[#F59E0B] text-sm">This will replace the current {currentRoundHeats.length} heats for this round.</p>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-[#A1A1AA] mt-1"
                      onClick={() => setForceShowSetup(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
                <Shuffle className="w-16 h-16 text-[#27272a] mx-auto mb-4" />
                <h3 className="font-['Playfair_Display'] text-2xl font-bold text-white mb-2">Setup Heats - {currentRoundName}</h3>
                <p className="text-[#A1A1AA] mb-6">
                  You have <strong className="text-[#FFD700]">{readyLeaders.length}</strong> Leaders and <strong className="text-[#FF007F]">{readyFollowers.length}</strong> Followers ready.
                  {selectedRound > 1 && (
                    <span className="block text-xs mt-1 text-[#52525B]">
                      (Only advancing dancers from previous round)
                    </span>
                  )}
                </p>
                
                <div className="max-w-md mx-auto space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#A1A1AA] block mb-2">Number of Heats</Label>
                      <Input
                        type="number"
                        min="1"
                        max="20"
                        defaultValue="5"
                        id="numHeats"
                        className="bg-black/20 border-white/10 text-white text-center text-lg"
                      />
                    </div>
                    <div>
                      <Label className="text-[#A1A1AA] block mb-2">Couples per Heat</Label>
                      <Input
                        type="number"
                        min="1"
                        max="35"
                        defaultValue="5"
                        id="couplesPerHeat"
                        className="bg-black/20 border-white/10 text-white text-center text-lg"
                      />
                    </div>
                  </div>
                  <Button 
                    onClick={() => {
                      const numHeats = parseInt(document.getElementById('numHeats').value) || 5;
                      const couplesPerHeat = parseInt(document.getElementById('couplesPerHeat').value) || 5;
                      handleCreateHeats(numHeats, couplesPerHeat);
                    }}
                    className="w-full btn-gold py-4 text-lg"
                  >
                    <Shuffle className="w-5 h-5 mr-2" />
                    Create {document.getElementById('numHeats')?.value || 5} Heats
                  </Button>
                  <button
                    onClick={() => setShowManualBuilder(true)}
                    className="w-full text-center text-sm text-[#FFD700] hover:underline mt-2"
                    data-testid="open-manual-builder-initial"
                  >
                    or build heats <b>manually</b> — pick each dancer per heat
                  </button>
                </div>
              </div>
            ) : (
              <>
                {/* Active Heat Banner */}
                {currentRoundHeats.find(h => h.status === 'active') && (
                  <div className="mb-6 p-4 bg-[#FF007F]/20 border border-[#FF007F]/30 rounded-xl">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-3 h-3 bg-[#FF007F] rounded-full animate-pulse"></div>
                        <div>
                          <p className="text-[#FF007F] text-sm font-medium uppercase tracking-wider">Active Heat</p>
                          <p className="text-white text-2xl font-bold">
                            Heat {currentRoundHeats.find(h => h.status === 'active')?.heat_number}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <a 
                          href={`/now-dancing/${competitionId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-4 py-2 bg-[#FF007F] text-white rounded-lg hover:bg-[#E5006E] transition-colors"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Now Dancing
                        </a>
                        <Button onClick={handleNextHeat} className="btn-gold">
                          <SkipForward className="w-4 h-4 mr-2" />
                          Next Heat
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* If no active heat, show Start Round button */}
                {!currentRoundHeats.find(h => h.status === 'active') && (
                  <div className="mb-6 p-4 bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-xl">
                    <div className="flex items-center justify-between">
                      <p className="text-white">Ready to start? Activate the first heat for judges to begin scoring.</p>
                      <div className="flex items-center gap-3">
                        <Button 
                          variant="outline" 
                          className="btn-outline" 
                          size="sm"
                          onClick={() => setForceShowSetup(true)}
                          data-testid="setup-heats-again-btn"
                        >
                          <Shuffle className="w-4 h-4 mr-2" />
                          Setup Heats Again
                        </Button>
                        <Button
                          variant="outline"
                          className="btn-outline"
                          size="sm"
                          onClick={() => setShowManualBuilder(true)}
                          data-testid="manual-heat-setup-btn"
                        >
                          <Wand2 className="w-4 h-4 mr-2 text-[#FFD700]" />
                          Manual Setup
                        </Button>
                        <Button onClick={handleStartRound} className="btn-gold">
                          <Play className="w-4 h-4 mr-2" />
                          Start Heat 1
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Progress Bar */}
                <div className="mb-6">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-[#A1A1AA]">Progress</span>
                    <span className="text-white">
                      {currentRoundHeats.filter(h => h.status === 'scored' || h.status === 'completed').length} / {currentRoundHeats.length} heats completed
                    </span>
                  </div>
                  <div className="h-2 bg-[#27272a] rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-[#FFD700] to-[#FF007F] transition-all"
                      style={{ 
                        width: `${(currentRoundHeats.filter(h => h.status === 'scored' || h.status === 'completed').length / currentRoundHeats.length) * 100}%` 
                      }}
                    />
                  </div>
                </div>

                {/* Heats List */}
                <div className="space-y-4">
                  {currentRoundHeats.map((heat) => (
                    <motion.div
                      key={heat.heat_id}
                      className={`glass p-6 rounded-xl transition-all ${
                        heat.status === 'active' 
                          ? 'border-2 border-[#FF007F] ring-2 ring-[#FF007F]/20' 
                          : ''
                      }`}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      data-testid={`heat-${heat.heat_id}`}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-4">
                          <div className="text-center">
                            <span className="text-[#A1A1AA] text-xs uppercase">Heat</span>
                            <p className="font-['Playfair_Display'] text-3xl font-bold text-[#FFD700]">{heat.heat_number}</p>
                          </div>
                          <div>
                            <p className="text-white font-medium">{heat.pairs?.length || 0} {heat.judge_dancing ? `${heat.showcase_role || ""}s` : "couples"}</p>
                            {heat.judge_dancing && (
                              <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-[#FFD700]/15 border border-[#FFD700]/40 text-[#FFD700] text-[10px] font-bold uppercase tracking-wider" data-testid={`heat-judge-dancing-badge-${heat.heat_number}`}>
                                <Wand2 className="w-3 h-3" />
                                Judge Dancing · {heat.showcase_role}s
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {heat.status === 'active' && (
                            <span className="flex items-center gap-2 px-3 py-1 bg-[#FF007F] text-white rounded-full text-sm font-bold animate-pulse">
                              <span className="w-2 h-2 bg-white rounded-full"></span>
                              LIVE
                            </span>
                          )}
                          {(heat.status === 'scored' || heat.status === 'completed') && (
                            <span className="px-3 py-1 bg-green-500/20 text-green-500 rounded-full text-xs font-medium">
                              Done
                            </span>
                          )}
                          {heat.status === 'pending' && (
                            <span className="px-3 py-1 bg-[#A1A1AA]/20 text-[#A1A1AA] rounded-full text-xs font-medium">
                              Waiting
                            </span>
                          )}
                          {!heat.judge_dancing && (
                            <HeatActionsMenu
                              heat={heat}
                              otherHeats={currentRoundHeats.filter((h) => h.heat_id !== heat.heat_id && !h.judge_dancing)}
                              api={API}
                              headers={getAuthHeaders()}
                              onChanged={fetchAll}
                              onOpenAdd={() => setAddPairHeat(heat)}
                            />
                          )}
                        </div>
                      </div>
                      
                      {/* Pairs in heat */}
                      <div className="grid gap-2">
                        {heat.pairs?.map((pair, idx) => (
                          <div key={idx} className="flex items-center gap-4 p-3 bg-black/20 rounded-lg">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="w-12 h-12 rounded-lg bg-[#FFD700]/20 flex items-center justify-center shrink-0">
                                <span className="text-[#FFD700] font-bold text-lg">#{pair.leader_bib || '?'}</span>
                              </div>
                              <p className="text-white truncate">{pair.leader_name || <span className="text-[#52525B] italic">empty</span>}</p>
                            </div>
                            <span className="text-[#52525B] text-xl">—</span>
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="w-12 h-12 rounded-lg bg-[#FF007F]/20 flex items-center justify-center shrink-0">
                                <span className="text-[#FF007F] font-bold text-lg">#{pair.follower_bib || '?'}</span>
                              </div>
                              <p className="text-white truncate">{pair.follower_name || <span className="text-[#52525B] italic">empty</span>}</p>
                            </div>
                            {!heat.judge_dancing && (
                              <PairActionsMenu
                                heat={heat}
                                pairIndex={idx}
                                otherHeats={currentRoundHeats.filter((h) => h.heat_id !== heat.heat_id && !h.judge_dancing)}
                                api={API}
                                headers={getAuthHeaders()}
                                onChanged={fetchAll}
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* All Heats Completed - Advance Section or Final Congratulations */}
                {currentRoundHeats.length > 0 && 
                 currentRoundHeats.every(h => h.status === 'scored' || h.status === 'completed') && (
                  <div className="mt-8 glass p-6 rounded-xl border-2 border-[#FFD700]/30">
                    {isFinalRound ? (
                      <>
                        <div className="text-center py-4">
                          <Trophy className="w-16 h-16 text-[#FFD700] mx-auto mb-4" />
                          <h3 className="font-['Playfair_Display'] text-2xl font-bold text-white mb-2">
                            All Heats Completed!
                          </h3>
                          <p className="text-[#FFD700] text-lg font-medium mb-2">
                            Congratulations to the winners!
                          </p>
                          <p className="text-[#A1A1AA] text-sm">
                            Check the Rankings tab for final results or download the PDF.
                          </p>
                        </div>
                      </>
                    ) : (
                      <>
                        <h3 className="font-['Playfair_Display'] text-xl font-bold text-white mb-2">
                          All Heats Completed!
                        </h3>
                        <p className="text-[#A1A1AA] mb-4">
                          Choose how many dancers from each role will advance to {competition?.rounds?.[selectedRound] || 'the next round'}.
                        </p>
                        <div className="flex items-center gap-4">
                          <Button
                            className="btn-gold"
                            onClick={() => setShowQualifyDialog(true)}
                            data-testid="heats-advance-btn"
                          >
                            <Award className="w-4 h-4 mr-2" />
                            Setup Qualification
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* Rankings Tab */}
          <TabsContent value="rankings">
            {/* Header + view mode selector */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <h2 className="text-white font-bold text-lg">Rankings — {currentRoundName}</h2>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant={rankingsView === "round" ? "default" : "outline"}
                  className={rankingsView === "round" ? "btn-gold" : "btn-outline"}
                  onClick={() => setRankingsView("round")}
                  data-testid="view-by-round-btn"
                >
                  By Round
                </Button>
                <Button
                  size="sm"
                  variant={rankingsView === "heat" ? "default" : "outline"}
                  className={rankingsView === "heat" ? "btn-gold" : "btn-outline"}
                  onClick={() => fetchByHeatRankings()}
                  data-testid="view-by-heat-btn"
                >
                  By Heat
                </Button>
                <Button
                  size="sm"
                  variant={rankingsView === "cumulative" ? "default" : "outline"}
                  className={rankingsView === "cumulative" ? "btn-gold" : "btn-outline"}
                  onClick={() => fetchCumulativeRankings()}
                  data-testid="view-cumulative-btn"
                >
                  Cumulative
                </Button>
                <img src="/jj-logo.png" alt="JackAndJillScore" className="h-8 opacity-30 rounded ml-2" />
              </div>
            </div>

            {/* === BY HEAT VIEW === */}
            {rankingsView === "heat" && byHeatData && (
              <div className="space-y-6" data-testid="rankings-by-heat">
                {byHeatData.heats.map((heat) => (
                  <div key={heat.heat_id} className="glass p-5 rounded-xl">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-['Playfair_Display'] text-lg font-bold text-white">
                        Heat {heat.heat_number}
                        <span className="ml-3 text-xs uppercase tracking-wider text-[#A1A1AA]">{heat.status}</span>
                      </h3>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="btn-outline"
                          onClick={() => downloadHeatPDF(heat.heat_id)}
                          data-testid={`heat-card-pdf-${heat.heat_number}`}
                        >
                          <Download className="w-3 h-3 mr-1" />
                          PDF
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="btn-outline"
                          onClick={() => downloadHeatXLSX(heat.heat_id)}
                          data-testid={`heat-card-xlsx-${heat.heat_number}`}
                        >
                          <FileSpreadsheet className="w-3 h-3 mr-1" />
                          Excel
                        </Button>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-[#FFD700] text-xs uppercase tracking-widest mb-2">Leaders</p>
                        <div className="space-y-1.5">
                          {heat.leaders.map((d) => (
                            <div key={d.dancer_id} className="flex items-center justify-between text-sm">
                              <span className="text-white">
                                #{d.rank} — {d.name} <span className="text-[#A1A1AA]">(#{d.bib_number})</span>
                              </span>
                              <span className="text-[#FFD700] font-bold">{(d.average_score || 0).toFixed(2)}</span>
                            </div>
                          ))}
                          {heat.leaders.length === 0 && <p className="text-[#52525B] text-xs">No leaders</p>}
                        </div>
                      </div>
                      <div>
                        <p className="text-[#FF007F] text-xs uppercase tracking-widest mb-2">Followers</p>
                        <div className="space-y-1.5">
                          {heat.followers.map((d) => (
                            <div key={d.dancer_id} className="flex items-center justify-between text-sm">
                              <span className="text-white">
                                #{d.rank} — {d.name} <span className="text-[#A1A1AA]">(#{d.bib_number})</span>
                              </span>
                              <span className="text-[#FF007F] font-bold">{(d.average_score || 0).toFixed(2)}</span>
                            </div>
                          ))}
                          {heat.followers.length === 0 && <p className="text-[#52525B] text-xs">No followers</p>}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {byHeatData.heats.length === 0 && (
                  <p className="text-[#A1A1AA] text-center py-8">No heats in this round.</p>
                )}
              </div>
            )}

            {/* === CUMULATIVE VIEW === */}
            {rankingsView === "cumulative" && cumulativeData && (
              <div className="grid md:grid-cols-2 gap-6" data-testid="rankings-cumulative">
                {[
                  { label: "Leaders", data: cumulativeData.leaders, color: "#FFD700" },
                  { label: "Followers", data: cumulativeData.followers, color: "#FF007F" },
                ].map((side) => (
                  <div key={side.label} className="glass p-5 rounded-xl">
                    <h3 className="font-['Playfair_Display'] text-lg font-bold mb-3" style={{ color: side.color }}>
                      {side.label} — Cumulative
                    </h3>
                    <div className="space-y-2">
                      {side.data.map((d) => (
                        <div key={d.dancer_id} className="flex items-center justify-between text-sm border-b border-white/5 py-1.5">
                          <div>
                            <span className="text-white font-medium">#{d.rank} — {d.name}</span>
                            <span className="text-[#52525B] ml-2 text-xs">
                              ({d.rounds_participated.length} round{d.rounds_participated.length !== 1 ? "s" : ""})
                            </span>
                          </div>
                          <span className="font-bold" style={{ color: side.color }}>
                            {d.cumulative_average.toFixed(2)}
                          </span>
                        </div>
                      ))}
                      {side.data.length === 0 && <p className="text-[#52525B] text-xs">No data</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* === BY-ROUND VIEW (existing) === */}
            {rankingsView === "round" && (
            <div className="grid md:grid-cols-2 gap-6">
              {/* Leaders Rankings */}
              <div className="glass p-6 rounded-xl">
                <h3 className="font-['Playfair_Display'] text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Award className="w-5 h-5 text-[#FFD700]" />
                  Leaders Rankings
                </h3>
                <div className="space-y-3">
                  {rankings.leaders.map((dancer, index) => (
                    <div
                      key={dancer.dancer_id}
                      className={`leaderboard-item ${index === 0 ? 'gold' : index === 1 ? 'silver' : index === 2 ? 'bronze' : ''}`}
                      data-testid={`leader-rank-${dancer.dancer_id}`}
                    >
                      <span className={`rank-number ${index < 3 ? 'text-[#FFD700]' : 'text-[#A1A1AA]'}`}>
                        {index + 1}
                      </span>
                      <div className="flex-1 ml-4">
                        <p className="text-white font-medium">
                          {dancer.name} <span className="text-[#FFD700]">(#{dancer.bib_number || '?'})</span>
                        </p>
                        <p className="text-[#A1A1AA] text-sm">{dancer.country}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[#FFD700] font-bold">{(dancer.combined_score ?? dancer.average_score ?? 0).toFixed(2)}</p>
                        <p className="text-[#52525B] text-xs">
                          {rankings.audience_voting_enabled && rankings.audience_voting_percentage > 0 ? (
                            <>J: {(dancer.average_score ?? 0).toFixed(1)} · A: {(dancer.audience_normalized ?? 5).toFixed(1)}</>
                          ) : (
                            <>avg score</>
                          )}
                        </p>
                      </div>
                    </div>
                  ))}
                  {rankings.leaders.length === 0 && <p className="text-[#52525B] text-center py-4">No scores yet</p>}
                </div>
              </div>
              {/* Followers Rankings */}
              <div className="glass p-6 rounded-xl">
                <h3 className="font-['Playfair_Display'] text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Award className="w-5 h-5 text-[#FF007F]" />
                  Followers Rankings
                </h3>
                <div className="space-y-3">
                  {rankings.followers.map((dancer, index) => (
                    <div
                      key={dancer.dancer_id}
                      className={`leaderboard-item ${index === 0 ? 'gold' : index === 1 ? 'silver' : index === 2 ? 'bronze' : ''}`}
                      data-testid={`follower-rank-${dancer.dancer_id}`}
                    >
                      <span className={`rank-number ${index < 3 ? 'text-[#FF007F]' : 'text-[#A1A1AA]'}`}>
                        {index + 1}
                      </span>
                      <div className="flex-1 ml-4">
                        <p className="text-white font-medium">
                          {dancer.name} <span className="text-[#FF007F]">(#{dancer.bib_number || '?'})</span>
                        </p>
                        <p className="text-[#A1A1AA] text-sm">{dancer.country}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[#FF007F] font-bold">{(dancer.combined_score ?? dancer.average_score ?? 0).toFixed(2)}</p>
                        <p className="text-[#52525B] text-xs">
                          {rankings.audience_voting_enabled && rankings.audience_voting_percentage > 0 ? (
                            <>J: {(dancer.average_score ?? 0).toFixed(1)} · A: {(dancer.audience_normalized ?? 5).toFixed(1)}</>
                          ) : (
                            <>avg score</>
                          )}
                        </p>
                      </div>
                    </div>
                  ))}
                  {rankings.followers.length === 0 && <p className="text-[#52525B] text-center py-4">No scores yet</p>}
                </div>
              </div>
            </div>
            )}

            {/* Advance Dancers Section - only show if NOT the final round */}
            {rankings.leaders.length > 0 && !isFinalRound && (
              <div className="mt-8 glass p-6 rounded-xl">
                <h3 className="font-['Playfair_Display'] text-lg font-bold text-white mb-4">
                  Advance to Next Round
                </h3>
                <p className="text-[#A1A1AA] mb-4">
                  Configure qualification: per-heat or global ranking, with independent Leaders / Followers counts.
                </p>
                <Button
                  className="btn-gold"
                  onClick={() => setShowQualifyDialog(true)}
                  data-testid="advance-dancers-btn"
                >
                  <Award className="w-4 h-4 mr-2" />
                  Setup Qualification
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Feedback Tab */}
          <TabsContent value="feedback">
            <FeedbackManager
              api={API}
              token={token}
              competitionId={competitionId}
              festivalName={competition?.festival_name}
              category={competition?.category}
            />
          </TabsContent>
        </Tabs>

        {/* QR Code Dialog - Generated on Frontend using window.location.origin */}
        <Dialog open={!!showQRDialog} onOpenChange={() => setShowQRDialog(null)}>
          <DialogContent className="bg-[#18181b] border-white/10 text-white max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-white text-center font-['Playfair_Display'] text-xl">
                {showQRDialog?.judgeName || "Judge"} - QR Code
              </DialogTitle>
            </DialogHeader>
            {showQRDialog && (
              <div className="text-center py-4">
                <p className="text-[#FFD700] text-lg font-bold mb-4">{showQRDialog.judgeName}</p>
                <div className="bg-white p-4 rounded-xl inline-block mb-4" id="judge-qr-container">
                  <QRCode
                    value={showQRDialog.loginUrl}
                    size={192}
                    level="M"
                    data-testid="judge-qr-svg"
                  />
                </div>
                <p className="text-[#A1A1AA] text-sm mb-2">
                  Scan this QR code to open the judge interface
                </p>
                <p className="text-[#00E5FF] text-xs mb-4 break-all">
                  {showQRDialog.loginUrl}
                </p>
                <div className="flex gap-2">
                  <Button
                    className="flex-1 btn-gold"
                    onClick={() => {
                      navigator.clipboard.writeText(showQRDialog.loginUrl);
                      toast.success("Judge login link copied!");
                    }}
                    data-testid="copy-judge-link-btn"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Link
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 btn-outline"
                    onClick={() => {
                      const svg = document.querySelector('#judge-qr-container svg');
                      if (svg) {
                        const svgData = new XMLSerializer().serializeToString(svg);
                        const canvas = document.createElement('canvas');
                        canvas.width = 256; canvas.height = 256;
                        const ctx = canvas.getContext('2d');
                        const img = new Image();
                        img.onload = () => {
                          ctx.fillStyle = 'white';
                          ctx.fillRect(0, 0, 256, 256);
                          ctx.drawImage(img, 0, 0, 256, 256);
                          const link = document.createElement('a');
                          link.download = `judge-qr-${showQRDialog.judgeName}.png`;
                          link.href = canvas.toDataURL('image/png');
                          link.click();
                        };
                        img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
                      }
                      toast.success("QR code downloaded!");
                    }}
                    data-testid="download-qr-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Save QR
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Reset Competition Dialog */}
        <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
          <DialogContent className="bg-[#18181b] border-white/10 text-white max-w-sm">
            <DialogHeader>
              <DialogTitle className="text-red-500 text-center">Reset Competition</DialogTitle>
            </DialogHeader>
            <div className="text-center py-4">
              <RotateCcw className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <p className="text-[#A1A1AA] mb-2">
                This will clear all heats, scores, and round progress. Your dancer list will remain unchanged.
              </p>
              <p className="text-red-400 text-sm font-bold mb-6">
                This action cannot be undone!
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 btn-outline"
                  onClick={() => setShowResetDialog(false)}
                  data-testid="cancel-reset-btn"
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                  onClick={handleResetCompetition}
                  data-testid="confirm-reset-btn"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Reset
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Audience Voting QR Dialog - Generated on Frontend */}
        <Dialog open={!!audienceQR} onOpenChange={() => setAudienceQR(null)}>
          <DialogContent className="bg-[#18181b] border-white/10 text-white max-w-sm">
            <DialogHeader>
              <DialogTitle className="font-['Playfair_Display'] text-xl text-white text-center">
                Audience Voting QR
              </DialogTitle>
            </DialogHeader>
            {audienceQR && (
              <div className="text-center py-4">
                <p className="text-[#FF007F] text-sm font-medium mb-4">
                  Scan to vote ({competition?.audience_voting_percentage}% of score)
                </p>
                <div className="bg-white p-4 rounded-xl inline-block mb-4" id="audience-qr-container">
                  <QRCode
                    value={audienceQR.voting_url}
                    size={192}
                    level="M"
                    data-testid="audience-qr-svg"
                  />
                </div>
                <p className="text-[#A1A1AA] text-xs mb-2 break-all">
                  {audienceQR.voting_url}
                </p>
                <div className="flex gap-2">
                  <Button
                    className="flex-1 btn-gold"
                    onClick={() => {
                      navigator.clipboard.writeText(audienceQR.voting_url);
                      toast.success("Voting link copied!");
                    }}
                    data-testid="copy-voting-link-btn"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy Link
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 btn-outline"
                    onClick={() => {
                      const svg = document.querySelector('#audience-qr-container svg');
                      if (svg) {
                        const svgData = new XMLSerializer().serializeToString(svg);
                        const canvas = document.createElement('canvas');
                        canvas.width = 256; canvas.height = 256;
                        const ctx = canvas.getContext('2d');
                        const img = new Image();
                        img.onload = () => {
                          ctx.fillStyle = 'white';
                          ctx.fillRect(0, 0, 256, 256);
                          ctx.drawImage(img, 0, 0, 256, 256);
                          const link = document.createElement('a');
                          link.download = 'audience-voting-qr.png';
                          link.href = canvas.toDataURL('image/png');
                          link.click();
                        };
                        img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
                      }
                    }}
                    data-testid="download-voting-qr-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Save QR
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Qualification Setup Dialog */}
        <Dialog open={showQualifyDialog} onOpenChange={setShowQualifyDialog}>
          <DialogContent className="bg-[#18181b] border-white/10 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="font-['Playfair_Display']">Setup Qualification</DialogTitle>
            </DialogHeader>
            <div className="space-y-5 mt-4">
              <div>
                <Label className="text-[#A1A1AA] mb-2 block">Qualification Mode</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    variant={qualifyMode === "global" ? "default" : "outline"}
                    className={qualifyMode === "global" ? "btn-gold" : "btn-outline"}
                    onClick={() => setQualifyMode("global")}
                    data-testid="qualify-mode-global"
                  >
                    Global Round
                  </Button>
                  <Button
                    variant={qualifyMode === "per_heat" ? "default" : "outline"}
                    className={qualifyMode === "per_heat" ? "btn-gold" : "btn-outline"}
                    onClick={() => setQualifyMode("per_heat")}
                    data-testid="qualify-mode-per-heat"
                  >
                    Per Heat
                  </Button>
                </div>
                <p className="text-[#52525B] text-xs mt-2">
                  {qualifyMode === "global"
                    ? "Top N from each role across the whole round."
                    : "Top N from each role within each heat."}
                </p>
              </div>

              {qualifyMode === "global" ? (
                <>
                  <div>
                    <Label className="text-[#FFD700]">Leaders to qualify</Label>
                    <Input
                      type="number" min="1" max="200"
                      value={qualifyLeaders}
                      onChange={(e) => setQualifyLeaders(parseInt(e.target.value) || 1)}
                      className="mt-1 bg-black/20 border-white/10 text-white"
                      data-testid="qualify-leaders-input"
                    />
                  </div>
                  <div>
                    <Label className="text-[#FF007F]">Followers to qualify</Label>
                    <Input
                      type="number" min="1" max="200"
                      value={qualifyFollowers}
                      onChange={(e) => setQualifyFollowers(parseInt(e.target.value) || 1)}
                      className="mt-1 bg-black/20 border-white/10 text-white"
                      data-testid="qualify-followers-input"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <Label className="text-[#FFD700]">Leaders qualifying per heat</Label>
                    <Input
                      type="number" min="0" max="50"
                      value={qualifyPerHeatLeaders}
                      onChange={(e) => setQualifyPerHeatLeaders(parseInt(e.target.value) || 0)}
                      className="mt-1 bg-black/20 border-white/10 text-white"
                      data-testid="qualify-per-heat-leaders-input"
                    />
                  </div>
                  <div>
                    <Label className="text-[#FF007F]">Followers qualifying per heat</Label>
                    <Input
                      type="number" min="0" max="50"
                      value={qualifyPerHeatFollowers}
                      onChange={(e) => setQualifyPerHeatFollowers(parseInt(e.target.value) || 0)}
                      className="mt-1 bg-black/20 border-white/10 text-white"
                      data-testid="qualify-per-heat-followers-input"
                    />
                  </div>
                </>
              )}

              <Button
                className="w-full btn-gold py-3"
                onClick={() => handleAdvanceDancers({
                  mode: qualifyMode,
                  count_leaders: qualifyLeaders,
                  count_followers: qualifyFollowers,
                  per_heat_leaders: qualifyPerHeatLeaders,
                  per_heat_followers: qualifyPerHeatFollowers,
                })}
                data-testid="qualify-submit-btn"
              >
                Advance Dancers
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Imbalance Strategy Dialog */}
        <Dialog open={showImbalanceDialog} onOpenChange={setShowImbalanceDialog}>
          <DialogContent className="bg-[#18181b] border-white/10 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="font-['Playfair_Display']">Uneven Roles</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              {pendingHeatGeneration && (
                <>
                <p className="text-[#A1A1AA]">
                  You have <span className="text-[#FFD700] font-bold">{pendingHeatGeneration.advancingLeaders} Leaders</span> and{" "}
                  <span className="text-[#FF007F] font-bold">{pendingHeatGeneration.advancingFollowers} Followers</span>.
                  How would you like to handle the imbalance?
                </p>
                <Button
                  variant="outline" className="w-full btn-outline justify-start text-left border-[#FFD700]/40"
                  onClick={() => {
                    setShowImbalanceDialog(false);
                    setShowManualBuilder(true);
                  }}
                  data-testid="imbalance-manual-btn"
                >
                  <div>
                    <p className="font-bold text-[#FFD700]">🎯 Manual Setup (recommended for uneven roles)</p>
                    <p className="text-xs text-[#52525B]">Define structure per heat (5L/3F, 4L/4F, …) — full control.</p>
                  </div>
                </Button>
                </>
              )}
              <Button
                variant="outline" className="w-full btn-outline justify-start text-left"
                onClick={async () => {
                  setShowImbalanceDialog(false);
                  await doGenerateHeats(pendingHeatGeneration.numHeats, pendingHeatGeneration.couplesPerHeat, pendingHeatGeneration.roundNum, "volunteers", null);
                }}
                data-testid="imbalance-volunteers-btn"
              >
                <div>
                  <p className="font-bold">Use Volunteers</p>
                  <p className="text-xs text-[#52525B]">Solo entries — pair with audience volunteers IRL. Volunteers don't get scored.</p>
                </div>
              </Button>

              {/* Second Chance — now with mode selector */}
              <div className="rounded-lg border border-white/10 p-3 space-y-3" data-testid="second-chance-section">
                <div>
                  <p className="font-bold text-white">Second Chance</p>
                  <p className="text-xs text-[#52525B]">Auto-pull the next-best dancers below the qualification cutoff from the previous round.</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setSecondChanceMode("global")}
                    className={`p-2 rounded-md text-xs border ${
                      secondChanceMode === "global"
                        ? "border-[#FFD700] bg-[#FFD700]/10 text-[#FFD700]"
                        : "border-white/10 text-[#A1A1AA] hover:border-white/20"
                    }`}
                    data-testid="sc-mode-global"
                  >
                    <p className="font-bold">Global Ranking</p>
                    <p className="opacity-70">From overall round rankings</p>
                  </button>
                  <button
                    onClick={() => setSecondChanceMode("per_heat")}
                    className={`p-2 rounded-md text-xs border ${
                      secondChanceMode === "per_heat"
                        ? "border-[#FFD700] bg-[#FFD700]/10 text-[#FFD700]"
                        : "border-white/10 text-[#A1A1AA] hover:border-white/20"
                    }`}
                    data-testid="sc-mode-per-heat"
                  >
                    <p className="font-bold">Per Heat</p>
                    <p className="opacity-70">Next-best from each prev heat</p>
                  </button>
                </div>
                <label className="flex items-center gap-2 text-xs text-[#A1A1AA]">
                  <input
                    type="checkbox"
                    className="accent-[#FFD700]"
                    checked={fillBothRoles}
                    onChange={(e) => setFillBothRoles(e.target.checked)}
                    data-testid="sc-fill-both-checkbox"
                  />
                  Fill BOTH roles to {pendingHeatGeneration ? (pendingHeatGeneration.numHeats * pendingHeatGeneration.couplesPerHeat) : "target"} dancers (otherwise only the smaller role)
                </label>
                <Button
                  className="w-full btn-gold"
                  onClick={async () => {
                    setShowImbalanceDialog(false);
                    await doGenerateHeats(
                      pendingHeatGeneration.numHeats,
                      pendingHeatGeneration.couplesPerHeat,
                      pendingHeatGeneration.roundNum,
                      "second_chance",
                      null,
                      { second_chance_mode: secondChanceMode, fill_both_roles: fillBothRoles },
                    );
                  }}
                  data-testid="imbalance-second-chance-btn"
                >
                  Apply Second Chance
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline" className="btn-outline"
                  onClick={async () => {
                    setShowImbalanceDialog(false);
                    await doGenerateHeats(pendingHeatGeneration.numHeats, pendingHeatGeneration.couplesPerHeat, pendingHeatGeneration.roundNum, "single_role", "Leader");
                  }}
                  data-testid="imbalance-leaders-only-btn"
                >
                  Leaders Only
                </Button>
                <Button
                  variant="outline" className="btn-outline"
                  onClick={async () => {
                    setShowImbalanceDialog(false);
                    await doGenerateHeats(pendingHeatGeneration.numHeats, pendingHeatGeneration.couplesPerHeat, pendingHeatGeneration.roundNum, "single_role", "Follower");
                  }}
                  data-testid="imbalance-followers-only-btn"
                >
                  Followers Only
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </main>

      {showCinematicReveal && (
        <CinematicPodiumReveal
          api={API}
          token={token}
          competitionId={competitionId}
          initialRound={selectedRound}
          onClose={() => setShowCinematicReveal(false)}
        />
      )}

      {addPairHeat && (
        <AddPairDialog
          heat={addPairHeat}
          open={!!addPairHeat}
          onOpenChange={(o) => !o && setAddPairHeat(null)}
          api={API}
          headers={getAuthHeaders()}
          onChanged={fetchAll}
        />
      )}

      {showManualBuilder && competition && (
        <ManualHeatBuilder
          api={API}
          headers={getAuthHeaders()}
          competition={competition}
          open={showManualBuilder}
          onOpenChange={setShowManualBuilder}
          onSaved={(roundNum) => {
            if (roundNum) {
              setSelectedRound(roundNum);
              setForceShowSetup(false);
            }
            fetchAll();
          }}
        />
      )}
    </div>
  );
}
